import { useLocation, useNavigate } from 'react-router-dom';
import Chip from '../../../components/Chip';
import { useGetUserDeletionRequestDetails, useResolveDeletionRequest } from '../../../queries/user';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActionButton } from '../../../components/Forms/ActionButton';
import Modal from '../../Submissions/components/Modal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const UserDeletionRequestDetails = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const requestId = state?.requestId;

    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [resolutionType, setResolutionType] = useState(''); // 'accept' or 'reject'

    const { data: requestData, isLoading } = useGetUserDeletionRequestDetails(requestId);
    const { mutateAsync: resolveRequest, isPending } = useResolveDeletionRequest();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: {
            message: '',
        }
    });

    const onSubmit = async (data) => {
        console.log(data);
        const submitData = {
            requestId: requestId,
            status: resolutionType === 'accept' ? 'approved' : 'rejected',
            reason: data.message,
        };

        await resolveRequest(submitData, {
            onSuccess: (res) => {
                if (res.error) {
                    toast.error('Something went wrong!');
                    return;
                }
                setResolveModalOpen(false);
                reset();
                toast.success(`User deletion request ${resolutionType === 'accept' ? 'accepted' : 'rejected'} successfully!`);
                setTimeout(() => {
                    navigate('/user/delete-list');
                }, 2000);
            },
            onError: (error) => {
                toast.error('Failed to resolve request: ' + (error.message || 'Unknown error'));
            }
        });
    };

    const handleResolveClick = (type) => {
        setResolutionType(type);
        setResolveModalOpen(true);
    };

    const getStatusChip = (request) => {
        if (request.isAccepted) {
            return <Chip value="Accepted" color="green" small={true} />;
        } else if (request.isRejected) {
            return <Chip value="Rejected" color="red" small={true} />;
        } else {
            return <Chip value="Pending" color="yellow" small={true} />;
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col w-full font-semibold text-base overflow-visible">
                <section className="flex flex-col justify-center px-5 mt-5 py-5 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                    <div className='text-center'>Loading...</div>
                </section>
            </div>
        );
    }

    if (!requestData) {
        return (
            <div className="flex flex-col w-full font-semibold text-base overflow-visible">
                <section className="flex flex-col justify-center px-5 mt-5 py-5 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                    <div className='text-center'>Request not found</div>
                </section>
            </div>
        );
    }

    const user = requestData.userId;

    return (
        <div className="flex flex-col w-full font-semibold text-base overflow-visible">
            <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                <h1 className="text-xl text-primary font-bold">User Deletion Request Details</h1>
                <div className="flex flex-col gap-4">
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Name:</label>
                            <p>{`${user?.name?.firstName || ''} ${user?.name?.middleName || ''} ${user?.name?.lastName || ''}`.trim() || '-'}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Display Name:</label>
                            <p>{user?.displayName || '-'}</p>
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Username:</label>
                            <p>{user?.username || '-'}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Email:</label>
                            <p>{user?.email || '-'}</p>
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Mobile:</label>
                            <p>{user?.mobile || '-'}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Request Status:</label>
                            {getStatusChip(requestData)}
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">User Status:</label>
                            <Chip value={user?.active ? 'Active' : 'Inactive'} color={user?.active ? 'green' : 'inactive'} small={true} />
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Green Ambassador:</label>
                            <Chip value={user?.isGreenAmbassador ? 'Yes' : 'No'} color={user?.isGreenAmbassador ? 'green' : 'inactive'} small={true} />
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">User Registered:</label>
                            <p>{new Date(user?.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Request Date:</label>
                            <p>{new Date(requestData.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Points:</label>
                            <p>{user?.points || 0}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Carbon Saved:</label>
                            <p>{user?.carbonKgSaved || 0} kg</p>
                        </div>
                    </div>
                    <div className='grid grid-cols-2'>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Age Group:</label>
                            <p>{user?.ageGroup || '-'}</p>
                        </div>
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Gender:</label>
                            <p>{user?.gender || '-'}</p>
                        </div>
                    </div>
                    {user?.issues && user.issues.length > 0 && (
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Interested Issues:</label>
                            <div className="flex flex-wrap gap-2">
                                {user?.issues?.length || 0}
                            </div>
                        </div>
                    )}
                    <div className='flex flex-col gap-1'>
                        <label className="font-semibold text-primary">Profile Picture:</label>
                        {user?.profilepic ? (
                            <img
                                src={user.profilepic}
                                alt="User Profile"
                                className="w-24 h-24 object-cover rounded-full cursor-pointer"
                                onClick={() => window.open(user.profilepic, '_blank')}
                            />
                        ) : (
                            <p className="text-base">No profile picture</p>
                        )}
                    </div>
                    {(requestData.isAccepted || requestData.isRejected) && requestData.reason && (
                        <div className="flex gap-1 bg-white p-3 rounded shadow-sm border-border shadow-primary">
                            <label className="font-bold text-primary text-nowrap">Resolution Comment:</label>
                            <p>{requestData.reason}</p>
                        </div>
                    )}
                    {(requestData.isAccepted || requestData.isRejected) && requestData.resolvedAt && (
                        <div className="flex gap-1">
                            <label className="font-semibold text-primary">Resolved Date:</label>
                            <p>{new Date(requestData.resolvedAt).toLocaleDateString()}</p>
                        </div>
                    )}
                </div>

                <Modal isOpen={resolveModalOpen} onClose={() => setResolveModalOpen(false)}>
                    <h1>Do you really want to {resolutionType} this deletion request?</h1>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-col items-start justify-end gap-1 mt-5">
                            <p>Resolution Message</p>
                            <textarea
                                className="border border-border rounded-md p-2 w-full text-base outline-none focus:outline-none resize-none"
                                rows={7}
                                placeholder={`Enter reason for ${resolutionType === 'accept' ? 'accepting' : 'rejecting'} this request...`}
                                {...register('message', {
                                    required: 'Resolution message is required.'
                                })}
                            />
                            {
                                errors.message && (
                                    <p className="text-red text-base">{errors.message.message}</p>
                                )
                            }
                        </div>
                        <div className='flex items-center justify-end mt-5'>
                            <ActionButton
                                text={`${resolutionType === 'accept' ? 'Accept' : 'Reject'} Request`}
                                variant={'submit'}
                                disabled={isPending}
                            />
                        </div>
                    </form>
                </Modal>

                {(!requestData.isAccepted && !requestData.isRejected) && (
                    <div className='flex items-center justify-end gap-3 mt-2'>
                        <ActionButton
                            text="Reject Request"
                            variant={'reset'}
                            disabled={isPending}
                            handleSubmit={() => handleResolveClick('reject')}
                        />
                        <ActionButton
                            text="Accept Request"
                            variant={'submit'}
                            disabled={isPending}
                            handleSubmit={() => handleResolveClick('accept')}
                        />
                    </div>
                )}
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </div>
    );
}

export default UserDeletionRequestDetails;